import {Injectable} from '@angular/core';
import {HttpBackend, HttpClient} from '@angular/common/http';
import {VERSION} from '../../../environments/version';
import {environment} from '../../../environments/environment';

export interface IAppConfig {
  title: string;
  homePageEnabled: boolean;
  issuerCode: string;
  issuerCountry: string;
  recaptcha: {
    enabled: boolean;
    siteKey: string;
  };
  localization: {
    supportedLanguages: string[];
    localizationCountry: string;
  };
  apiServer: {
    apiUrl: string;
    EoApiUrl: string,
    OrderApiUrl: string;
    taxManagerApiUrl: string;
    mobileApiUrl: string;
    productMangementUrl:string,
    messageStorageManagerApiUrl: string;
    changeLogsManagerUrl: string;
    notificationManagerUrl:string;
    emailNotificationManagerUrl:string;
    registryServiceUrl: string;
    auditLogsManagerUrl:string;
    apiVersion: string;
    swaggerPath: string;
    apiDocsPath: string;
  };
  order: {
    maxUidsPerOrder: number,
    unitMaxDigitalQuantity: number,
    aggregatedMaxDigitalQuantity: number,
    unitMaxPhysicalQuantity: number,
    unitMinPhysicalQuantity: number,
    unitPhysicalDeliveryEnabled: boolean;
    purchaseOrderEnabled: boolean;
    unitPhysicalRollSize: number;
    aggregatedDigitalQuantityRollSize: number,
    unitDigitalQuantityRollSize: number,
    enabled: boolean;
    codeCheckEnabled: boolean;
    paymentDetails: {
      recipient: string,
      address: string,
      registrationCode: string,
      bank: string,
      iban: string,
      price: number,
      currency: string
    }
  };
  economicOperator: {
    reliabilityEnabled: boolean;
    billingSystemIdEnabled: boolean;
    vatCountries: string[];
    vatNumberFormats: Map<string, string>;
  };
  passwordPolicy: {
    normalUser: {
      minLength: number,
      uppercaseCharacters: number,
      lowercaseCharacters: number,
      digits: number,
      specialCharacters: number,
      notRecentlyUsed: number
    },
    technicalUser: {
      minLength: number,
      uppercaseCharacters: number,
      lowercaseCharacters: number,
      digits: number,
      specialCharacters: number,
      notRecentlyUsed: number
    }
  };
  reporting: {
    maxDaysForGroupByDays: number,
    enabled: boolean
  };
  productCodes: {
    enabled: boolean;
  };
  mapUrl: string;
  exciseWarehouses: {
    enabled: string
  };
  productionRequests: {
    enabled: string
  };
  users: {
    enabled: boolean
  }
  changeLogs: {
    enabled: boolean
  }
  repository:{
    enabled : boolean
  }
  settings:{
    enabled : boolean
  }
  isApprovalNeededForProduct: {
    enabled: boolean
  }
}

export function initializeAppConfig(appConfig: AppConfigService) {
  return () => appConfig.load();
}

@Injectable({
  providedIn: 'root'
})
export class AppConfigService {
  static settings: IAppConfig;
  static appVersion: string;

  private httpClient: HttpClient;

  constructor(handler: HttpBackend) {
    this.httpClient = new HttpClient(handler);
  }

  load() {
    const gkeConfigPath = '/app/config/config.json';

    return new Promise<void>((resolve, reject) => {
      // First, try to load the GKE config
      this.httpClient.get<IAppConfig>(gkeConfigPath).toPromise().then((response) => {
        AppConfigService.settings = response;
        resolve();
      }).catch((response: any) => {
        // If loading the GKE config fails, try the local config
        console.warn(`Could not load file ${gkeConfigPath}, falling back to local config.`);
        let jsonFile = './assets/config.json';
        if (environment && environment.env && environment.env === 'iaas') {
          jsonFile = `./assets/config-${environment.env}.json`;
        }

        this.httpClient.get(jsonFile, {params: {v: VERSION.hash}}).toPromise().then((response: IAppConfig) => {
          AppConfigService.settings = response as IAppConfig;
          resolve();
        }).catch((response: any) => {
          console.error(`Could not load file ${jsonFile} : ${JSON.stringify(response)}`);
          reject(`Could not load file ${jsonFile} : ${JSON.stringify(response)}`);
        });
      });
    });
  }

  static get rootUrl(): string {
    return this.settings && this.settings.apiServer.apiUrl;
  }

  static get apiUrl(): string {
    return this.settings && this.settings.apiServer.apiUrl + this.settings.apiServer.apiVersion;
  }

  static get auditLogsManagerUrl(): string {
    return this.settings && this.settings.apiServer.auditLogsManagerUrl;
  }

  static get eoApiUrl(): string {
    return this.settings && this.settings.apiServer.EoApiUrl;
  }

  static get orderApiUrl(): string {
    return this.settings && this.settings.apiServer.OrderApiUrl;
  }

  static get userRoleUrl(): string {
    if (!this.settings || !this.settings.apiServer || !this.settings.apiServer.EoApiUrl) {
      return null;
    }
    return this.settings.apiServer.EoApiUrl + this.settings.apiServer.apiVersion + "/user-role";
  }

  static get productMangementUrl(): string {
    return this.settings && this.settings.apiServer.productMangementUrl;
  }

  static get taxManagerApiUrl(): string {
    return this.settings && this.settings.apiServer.taxManagerApiUrl;
  }

  static get mobileApiUrl(): string {
    return this.settings && this.settings.apiServer.mobileApiUrl;
  }

  static get changeLogsManagerUrl(): string {
    return this.settings && this.settings.apiServer.changeLogsManagerUrl;
  }

  static get notificationManagerUrl(): string {
    return this.settings && this.settings.apiServer.notificationManagerUrl;
  }

  static get emailNotificationManagerUrl(): string {
    return this.settings && this.settings.apiServer.emailNotificationManagerUrl;
  }

  static get registryServiceUrl(): string {
    return this.settings && this.settings.apiServer.registryServiceUrl;
  }

  static get messageStorageManagerApiUrl(): string {
    return this.settings && this.settings.apiServer.messageStorageManagerApiUrl;
  }

  static get apiDocsPath(): string {
    return this.settings && this.settings.apiServer.apiDocsPath;
  }
}
